import React, { useEffect, useRef, useState } from 'react';
import {
  useListCandidatesQuery,
  useListCandidateCitiesQuery,
  useListCandidateSourcesQuery,
  useRunIngestionMutation,
  useIngestionStatusQuery,
  type CandidateStatusValue,
} from '../../redux/candidates/candidates.api';
import { FilterSelect } from './FilterSelect';

const PAGE_SIZE = 20;
const PAGE_JUMP = 5;

const STATUS_OPTIONS: Array<{ value: CandidateStatusValue; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const formatWhen = (dateIso: string | null, time: string | null): string => {
  if (!dateIso) return '—';
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return dateIso;
  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return time ? `${dateStr} · ${time}` : dateStr;
};

export const AdminCandidates = (): React.JSX.Element => {
  const [status, setStatus] = useState<CandidateStatusValue>('pending');
  const [city, setCity] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [runNote, setRunNote] = useState<string | null>(null);

  const { data, isLoading, isFetching, error, refetch } =
    useListCandidatesQuery({
      status,
      city: city || undefined,
      source: source || undefined,
      page,
      limit: PAGE_SIZE,
    });

  // City/source options for the dropdowns — everything present at the current
  // status, with counts. Independent of the selected value so the lists stay
  // stable while filtering.
  const { data: cityOptions } = useListCandidateCitiesQuery({ status });
  const { data: sourceOptions } = useListCandidateSourcesQuery({ status });

  const [runIngestion, { isLoading: isRunning }] = useRunIngestionMutation();

  // Poll the run progress every 2s while a run is active (and once on mount, so
  // a run started elsewhere — cron or another admin — still shows up). Polling
  // stops itself once the server reports the run finished.
  const [polling, setPolling] = useState(false);
  const { data: ingestion } = useIngestionStatusQuery(undefined, {
    pollingInterval: polling ? 2000 : 0,
  });
  const wasRunning = useRef(false);
  useEffect(() => {
    const running = !!ingestion?.running;
    if (running) setPolling(true);
    else if (ingestion) setPolling(false);
    // A run just finished → pull in the freshly discovered candidates.
    if (wasRunning.current && !running) refetch();
    wasRunning.current = running;
  }, [ingestion?.running, ingestion, refetch]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasItems = data && data.items.length > 0;
  const empty = data && data.items.length === 0;

  // Keep the page-number input in sync when page changes from buttons/clamping.
  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  // If the result set shrinks below the current page (e.g. a city filter, or an
  // approve/reject), snap back to the last valid page. Guarded on a settled
  // response so an in-flight page change isn't mistaken for an out-of-range page.
  useEffect(() => {
    if (data && !isFetching && page > totalPages) {
      setPage(totalPages);
    }
  }, [data, isFetching, page, totalPages]);

  const goToPage = (target: number): void => {
    setPage(Math.min(totalPages, Math.max(1, target)));
  };

  const commitPageInput = (): void => {
    const parsed = Number.parseInt(pageInput, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(page));
      return;
    }
    goToPage(parsed);
  };

  const handleRun = async (): Promise<void> => {
    if (
      !window.confirm(
        'Run ingestion now? This fetches the live source sites and runs in the background for a few minutes. New events land here as Pending — refresh the list shortly.',
      )
    ) {
      return;
    }
    setRunNote(null);
    try {
      const res = await runIngestion().unwrap();
      setRunNote(
        res.status === 'already-running'
          ? 'A run is already in progress.'
          : 'Ingestion started in the background.',
      );
      setPolling(true);
      setStatus('pending');
      setCity('');
      setSource('');
      setPage(1);
    } catch {
      setRunNote('Could not start ingestion. Check server logs.');
    }
  };

  const ingestionBusy = isRunning || !!ingestion?.running;
  const ingPct =
    ingestion && ingestion.totalSources
      ? Math.round((ingestion.completedSources / ingestion.totalSources) * 100)
      : 0;
  const ingInserted =
    ingestion?.sources.reduce((n, s) => n + (s.inserted ?? 0), 0) ?? 0;
  const ingUpdated =
    ingestion?.sources.reduce((n, s) => n + (s.updated ?? 0), 0) ?? 0;
  const ingFailed = ingestion?.sources.filter((s) => s.error).length ?? 0;
  const showProgress = !!ingestion && (ingestion.running || !!ingestion.finishedAt);

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Candidates</h2>
          {data && (
            <span className="admin-users__count">
              {data.total.toLocaleString()} {status}
              {city ? ` · ${city}` : ''}
              {source ? ` · ${source}` : ''}
            </span>
          )}
        </div>
        <div className="admin-events__filters">
          <select
            className="admin-events__status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as CandidateStatusValue);
              // Cities/sources differ per status, so clear both on switch.
              setCity('');
              setSource('');
              setPage(1);
            }}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <FilterSelect
            value={city}
            onChange={(next) => {
              setCity(next);
              setPage(1);
            }}
            options={(cityOptions ?? []).map((c) => ({
              value: c.city,
              label: c.city,
              count: c.count,
            }))}
            allLabel="All cities"
            searchable
            searchPlaceholder="Search cities…"
            ariaLabel="Filter by city"
          />
          <FilterSelect
            value={source}
            onChange={(next) => {
              setSource(next);
              setPage(1);
            }}
            options={(sourceOptions ?? []).map((s) => ({
              value: s.source,
              label: s.source,
              count: s.count,
            }))}
            allLabel="All sources"
            ariaLabel="Filter by source"
          />
          <button
            type="button"
            className="admin-users__page-btn"
            onClick={handleRun}
            disabled={ingestionBusy}
          >
            {ingestionBusy ? 'Running…' : 'Run ingestion'}
          </button>
        </div>
      </div>

      {runNote && (
        <div className="admin-users__count" style={{ marginBottom: 12 }}>
          {runNote}
        </div>
      )}

      {showProgress && ingestion && (
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              marginBottom: 4,
            }}
          >
            <span>
              {ingestion.running
                ? `Running… ${ingestion.currentSource ?? 'starting'} (${ingestion.completedSources}/${ingestion.totalSources} sources)`
                : `Done — ${ingestion.completedSources}/${ingestion.totalSources} sources`}
            </span>
            <span>{ingPct}%</span>
          </div>
          <div
            style={{
              height: 8,
              background: '#e5e7eb',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${ingPct}%`,
                height: '100%',
                background: ingestion.running ? '#3b82f6' : '#10b981',
                transition: 'width .3s ease',
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            {ingInserted} new · {ingUpdated} updated · {ingestion.staleRemoved}{' '}
            removed{ingFailed ? ` · ${ingFailed} failed` : ''}
          </div>
        </div>
      )}

      {error && (
        <div className="admin-users__error">
          Failed to load candidates. Try again.
        </div>
      )}

      {isLoading && !data && (
        <div className="admin-users__loading">Loading candidates…</div>
      )}

      {empty && (
        <div className="admin-tab-empty">
          <h2 className="admin-tab-empty__title">
            No {status} candidates{city ? ` in ${city}` : ''}
            {source ? ` from ${source}` : ''}
          </h2>
          <p className="admin-tab-empty__sub">
            {city || source
              ? 'Try different filters or clear them.'
              : status === 'pending'
                ? 'Run ingestion to discover events from the configured sources.'
                : `No ${status} candidates.`}
          </p>
        </div>
      )}

      {hasItems && (
        <>
          <div className="admin-users__pagination">
            <div className="admin-users__pagination-group">
              <button
                type="button"
                className="admin-users__page-btn admin-users__page-btn--compact"
                onClick={() => goToPage(1)}
                disabled={page <= 1 || isFetching}
                aria-label="First page"
                title="First page"
              >
                «
              </button>
              <button
                type="button"
                className="admin-users__page-btn admin-users__page-btn--compact"
                onClick={() => goToPage(page - PAGE_JUMP)}
                disabled={page <= 1 || isFetching}
                aria-label={`Back ${PAGE_JUMP} pages`}
                title={`Back ${PAGE_JUMP} pages`}
              >
                -{PAGE_JUMP}
              </button>
              <button
                type="button"
                className="admin-users__page-btn"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || isFetching}
              >
                ← Prev
              </button>
            </div>

            <span className="admin-users__page-info">
              Page{' '}
              <input
                type="number"
                className="admin-users__page-input"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitPageInput();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                disabled={isFetching}
                aria-label="Go to page"
              />{' '}
              of {totalPages}
            </span>

            <div className="admin-users__pagination-group">
              <button
                type="button"
                className="admin-users__page-btn"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || isFetching}
              >
                Next →
              </button>
              <button
                type="button"
                className="admin-users__page-btn admin-users__page-btn--compact"
                onClick={() => goToPage(page + PAGE_JUMP)}
                disabled={page >= totalPages || isFetching}
                aria-label={`Forward ${PAGE_JUMP} pages`}
                title={`Forward ${PAGE_JUMP} pages`}
              >
                +{PAGE_JUMP}
              </button>
              <button
                type="button"
                className="admin-users__page-btn admin-users__page-btn--compact"
                onClick={() => goToPage(totalPages)}
                disabled={page >= totalPages || isFetching}
                aria-label="Last page"
                title="Last page"
              >
                »
              </button>
            </div>
          </div>

          <div className="admin-users__table-wrap">
            <table className="admin-users__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Source</th>
                  <th>When</th>
                  <th>City</th>
                  <th>Fee</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {data!.items.map((c) => (
                  <tr
                    key={c.id}
                    className="admin-users__row admin-users__row--clickable"
                    onClick={() =>
                      window.open(
                        `/admin/dashboard/candidates/${c.id}`,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  >
                    <td>
                      <div className="admin-users__name">
                        <span>{c.name}</span>
                      </div>
                    </td>
                    <td>{c.source}</td>
                    <td>{formatWhen(c.startDate, c.startTime)}</td>
                    <td>{c.location?.city || '—'}</td>
                    <td>
                      {c.fee != null
                        ? `${c.fee} ${c.currency ?? ''}`.trim()
                        : '—'}
                    </td>
                    <td>{c.category || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isFetching && !isLoading && (
              <div className="admin-users__fetching-overlay">Updating…</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
